/**
 * 视频处理类
 * 处理单个元素的视频创建和管理
 */
class VideoHandler {
	constructor(element) {
		// 验证传入的 element 是否为有效的 DOM 元素
		if (!element || !(element instanceof Element)) {
			throw new Error('VideoHandler requires a valid DOM element');
		}

		this.element = element;
		this.video = null;
		this.observer = null;
		this.autoplay = this.element.dataset.autoplay === 'true';
		this.wordkvideo = this.element.closest('.workd-video');

		// 检查是否有移动端视频
		this.hasMobileVideo = !!(element.dataset.videoM || element.dataset.posterM);
	}

	/**
	 * 判断是否为移动端
	 */
	isMobile() {
		return window.innerWidth <= 640;
	}

	/**
	 * 根据 URL 获取视频 MIME 类型
	 */
	getVideoType(url) {
		const ext = url.split('.').pop().toLowerCase().split('?')[0];
		const typeMap = {
			mp4: 'video/mp4',
			webm: 'video/webm',
			ogg: 'video/ogg',
			ogv: 'video/ogg',
			mov: 'video/quicktime',
			avi: 'video/x-msvideo',
			m4v: 'video/x-m4v',
		};
		return typeMap[ext] || 'video/mp4';
	}

	/**
	 * 创建视频元素
	 */
	createVideo() {
		// 如果视频已存在，不重复创建
		if (this.video) {
			return;
		}

		// 获取对应的视频源和海报
		let videoSrc, posterSrc;

		if (this.hasMobileVideo) {
			// 有移动端视频，根据宽度选择
			const isMobile = this.isMobile();
			videoSrc = isMobile ? this.element.dataset.videoM : this.element.dataset.videoPc;
			posterSrc = isMobile ? this.element.dataset.posterM : this.element.dataset.posterPc;
		} else {
			// 没有移动端视频，直接使用 PC 版本
			videoSrc = this.element.dataset.videoPc;
			posterSrc = this.element.dataset.posterPc;
		}

		// 从 element 动态获取自动播放状态
		const autoplay = this.element.dataset.autoplay === 'true';

		if (!videoSrc) {
			console.warn('VideoHandler: No video source found for element', this.element);
			return;
		}

		console.log('VideoHandler: Creating video', { videoSrc, posterSrc, autoplay });

		// 创建 video 元素
		this.video = document.createElement('video');
		this.video.className = 'absolute top-0 left-0 w-full h-full object-cover';
		this.video.muted = true;
		this.video.loop = true;
		this.video.playsInline = true;

		// 使用 setAttribute 确保属性生效（特别是在 iOS Safari 中）
		this.video.setAttribute('muted', '');
		this.video.setAttribute('loop', '');
		this.video.setAttribute('playsinline', '');
		this.video.setAttribute('webkit-playsinline', '');
		this.video.setAttribute('preload', 'metadata');

		if (this.autoplay) {
			this.video.autoplay = true;
			this.video.setAttribute('autoplay', '');
		}

		if (posterSrc) {
			this.video.poster = posterSrc;
		}

		// 创建 source 元素并设置 type
		const source = document.createElement('source');
		source.src = videoSrc;
		source.type = this.getVideoType(videoSrc);
		this.video.appendChild(source);

		// 添加错误处理
		this.video.addEventListener('error', (e) => {
			console.error('VideoHandler: Video load error', {
				element: this.element,
				videoSrc,
				error: e,
				videoError: this.video.error,
			});
		});

		// 添加到容器
		this.element.appendChild(this.video);

		// 加载视频
		this.video.load();

		console.log('VideoHandler: Video element created and appended', this.video);

		return this.video;
	}

	/**
	 * 移除视频元素
	 */
	removeVideo() {
		if (!this.autoplay) {
			this.wordkvideo.dataset.play = 'false';
			this.element.dataset.autoplay = 'false';
		}
		if (this.video) {
			// 获取对应的海报图片
			let posterSrc;
			if (this.hasMobileVideo) {
				const isMobile = this.isMobile();
				posterSrc = isMobile ? this.element.dataset.posterM : this.element.dataset.posterPc;
			} else {
				posterSrc = this.element.dataset.posterPc;
			}

			// 设置背景图为海报
			if (posterSrc) {
				this.element.style.backgroundImage = `url('${posterSrc}')`;
				// this.element.style.backgroundSize = 'cover';
				// this.element.style.backgroundPosition = 'center';
				console.log('VideoHandler: Set background image to poster', posterSrc);
			}

			this.video.pause();
			// 移除所有 source 子元素
			while (this.video.firstChild) {
				this.video.removeChild(this.video.firstChild);
			}
			this.video.remove();
			this.video = null;
		}
	}

	/**
	 * 初始化视口监听
	 */
	init() {
		// 验证 element 是否为有效的 DOM 节点
		if (!this.element || !(this.element instanceof Element)) {
			console.error('VideoHandler: Invalid element provided', this.element);
			return;
		}

		const observerOptions = {
			root: null,
			rootMargin: '100px', // 提前 100px 开始加载
			threshold: 0,
		};

		this.observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					// 元素进入视口，创建视频
					this.createVideo();
				} else {
					// 元素离开视口，移除视频
					this.removeVideo();
				}
			});
		}, observerOptions);

		// 开始观察元素
		this.observer.observe(this.element);
	}

	/**
	 * 销毁处理器
	 */
	destroy() {
		if (this.observer) {
			this.observer.disconnect();
			this.observer = null;
		}
		this.removeVideo();
	}
}
/**
 * 批量初始化视频处理器
 * @param {string|NodeList|Array} selector - CSS 选择器字符串或元素列表
 * @returns {Array} 返回 VideoHandler 实例数组
 */
export function initVideoHandlers(selector) {
	let elements;

	// 处理不同类型的参数
	if (typeof selector === 'string') {
		elements = document.querySelectorAll(selector);
	} else if (selector instanceof NodeList || Array.isArray(selector)) {
		elements = selector;
	} else {
		console.error('initVideoHandlers: Invalid selector type');
		return [];
	}

	const handlers = [];

	elements.forEach((element) => {
		try {
			const handler = new VideoHandler(element);
			handler.init();
			handlers.push(handler);
		} catch (error) {
			console.error('Failed to initialize VideoHandler:', error, element);
		}
	});

	return handlers;
}
export { VideoHandler };
